/**
 * Admin Client API Client
 * API methods for admin client operations
 */

import { apiClient } from '../client';

export interface ClientListParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: 'active' | 'inactive' | 'suspended' | 'closed';
  groupId?: number;
  sortBy?: 'id' | 'email' | 'name' | 'createdAt' | 'lastLogin';
  sortOrder?: 'asc' | 'desc';
}

export interface CreateClientData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  companyName?: string;
  address1?: string;
  address2?: string;
  city?: string;
  state?: string;
  postcode?: string;
  countryCode?: string;
  phoneNumber?: string;
  status?: 'active' | 'inactive' | 'suspended' | 'closed';
  groupId?: number;
  currencyId?: number;
  languageId?: number;
  taxExempt?: boolean;
  creditBalance?: number;
  notes?: string;
  marketingEmails?: boolean;
  sendWelcomeEmail?: boolean;
}

export interface UpdateClientData {
  email?: string;
  firstName?: string;
  lastName?: string;
  companyName?: string;
  address1?: string;
  address2?: string;
  city?: string;
  state?: string;
  postcode?: string;
  countryCode?: string;
  phoneNumber?: string;
  status?: 'active' | 'inactive' | 'suspended' | 'closed';
  groupId?: number | null;
  currencyId?: number;
  languageId?: number;
  taxExempt?: boolean;
  notes?: string;
  marketingEmails?: boolean;
}

export interface AdjustCreditData {
  amount: number;
  description: string;
  type: 'add' | 'subtract';
}

export interface CreateContactData {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber?: string;
  permissions?: Record<string, boolean>;
  receiveEmails?: boolean;
}

export const clientsAdminApi = {
  /**
   * List all clients
   */
  list: async (params?: ClientListParams) => {
    const response = await apiClient.get('/admin/clients', { params });
    return response.data;
  },

  /**
   * Get single client by ID
   */
  get: async (clientId: number) => {
    const response = await apiClient.get(`/admin/clients/${clientId}`);
    return response.data;
  },

  /**
   * Create new client
   */
  create: async (data: CreateClientData) => {
    const response = await apiClient.post('/admin/clients', data);
    return response.data;
  },

  /**
   * Update client
   */
  update: async (clientId: number, data: UpdateClientData) => {
    const response = await apiClient.put(`/admin/clients/${clientId}`, data);
    return response.data;
  },

  /**
   * Delete client
   */
  delete: async (clientId: number) => {
    const response = await apiClient.delete(`/admin/clients/${clientId}`);
    return response.data;
  },

  /**
   * Adjust client credit balance
   */
  adjustCredit: async (clientId: number, data: AdjustCreditData) => {
    const response = await apiClient.post(`/admin/clients/${clientId}/credit`, data);
    return response.data;
  },

  /**
   * List client contacts
   */
  listContacts: async (clientId: number) => {
    const response = await apiClient.get(`/admin/clients/${clientId}/contacts`);
    return response.data;
  },

  /**
   * Create contact for client
   */
  createContact: async (clientId: number, data: CreateContactData) => {
    const response = await apiClient.post(`/admin/clients/${clientId}/contacts`, data);
    return response.data;
  },

  /**
   * Update contact
   */
  updateContact: async (clientId: number, contactId: number, data: Partial<CreateContactData>) => {
    const response = await apiClient.put(`/admin/clients/${clientId}/contacts/${contactId}`, data);
    return response.data;
  },

  /**
   * Delete contact
   */
  deleteContact: async (clientId: number, contactId: number) => {
    const response = await apiClient.delete(`/admin/clients/${clientId}/contacts/${contactId}`);
    return response.data;
  },
};
